import React, { useEffect } from 'react';
import { Form, Input, Select, DatePicker, Row, Col, Divider } from 'antd';
import dayjs from 'dayjs';
import { Employee, Department, Position } from '../types';

const { Option } = Select;

interface EmployeeFormProps {
  form: ReturnType<typeof Form.useForm>[0];
  initial?: Partial<Employee>;
  departments: Department[];
  positions: Position[];
}

const EmployeeForm: React.FC<EmployeeFormProps> = ({ form, initial, departments, positions }) => {
  useEffect(() => {
    if (initial) {
      form.setFieldsValue({
        ...initial,
        hireDate: initial.hireDate ? dayjs(initial.hireDate) : undefined,
        terminationDate: initial.terminationDate ? dayjs(initial.terminationDate) : undefined,
      });
    }
  }, [form, initial]);

  return (
    <Form form={form} layout="vertical" size="middle">
      <Divider orientation="left" plain>Personal Information</Divider>
      <Row gutter={16}>
        <Col xs={24} sm={8}>
          <Form.Item name="firstName" label="First Name" rules={[{ required: true }]}>
            <Input placeholder="First name" />
          </Form.Item>
        </Col>
        <Col xs={24} sm={8}>
          <Form.Item name="middleName" label="Middle Name">
            <Input placeholder="Middle name" />
          </Form.Item>
        </Col>
        <Col xs={24} sm={8}>
          <Form.Item name="lastName" label="Last Name" rules={[{ required: true }]}>
            <Input placeholder="Last name" />
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={16}>
        <Col xs={24} sm={12}>
          <Form.Item name="email" label="Email" rules={[{ type: 'email' }]}>
            <Input placeholder="email@example.com" />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12}>
          <Form.Item name="phoneNumber" label="Phone Number">
            <Input placeholder="+255..." />
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={16}>
        <Col xs={24} sm={12}>
          <Form.Item name="nationalId" label="National ID">
            <Input placeholder="National ID number" />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12}>
          <Form.Item name="address" label="Address">
            <Input placeholder="Physical address" />
          </Form.Item>
        </Col>
      </Row>

      <Divider orientation="left" plain>Employment Details</Divider>
      <Row gutter={16}>
        <Col xs={24} sm={8}>
          <Form.Item name="employeeNumber" label="Employee Number" rules={[{ required: true }]}>
            <Input placeholder="EMP-001" />
          </Form.Item>
        </Col>
        <Col xs={24} sm={8}>
          <Form.Item name="employmentType" label="Employment Type" rules={[{ required: true }]}>
            <Select placeholder="Select type">
              <Option value="FULL_TIME">Full Time</Option>
              <Option value="PART_TIME">Part Time</Option>
              <Option value="CONTRACT">Contract</Option>
              <Option value="INTERN">Intern</Option>
            </Select>
          </Form.Item>
        </Col>
        <Col xs={24} sm={8}>
          <Form.Item name="status" label="Status" rules={[{ required: true }]}>
            <Select placeholder="Select status">
              <Option value="ACTIVE">Active</Option>
              <Option value="INACTIVE">Inactive</Option>
              <Option value="ON_LEAVE">On Leave</Option>
              <Option value="TERMINATED">Terminated</Option>
            </Select>
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={16}>
        <Col xs={24} sm={8}>
          <Form.Item name="hireDate" label="Hire Date" rules={[{ required: true }]}>
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>
        </Col>
        <Col xs={24} sm={8}>
          <Form.Item name="departmentId" label="Department">
            <Select placeholder="Select department" allowClear showSearch optionFilterProp="children">
              {departments.map((d) => (
                <Option key={d.id} value={d.id}>{d.name}</Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
        <Col xs={24} sm={8}>
          <Form.Item name="positionId" label="Position">
            <Select placeholder="Select position" allowClear showSearch optionFilterProp="children">
              {positions.map((p) => (
                <Option key={p.id} value={p.id}>{p.title}</Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
      </Row>

      <Divider orientation="left" plain>Emergency Contact</Divider>
      <Row gutter={16}>
        <Col xs={24} sm={12}>
          <Form.Item name="emergencyContactName" label="Emergency Contact Name">
            <Input placeholder="Contact name" />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12}>
          <Form.Item name="emergencyContactPhone" label="Emergency Contact Phone">
            <Input placeholder="+255..." />
          </Form.Item>
        </Col>
      </Row>
    </Form>
  );
};

export default EmployeeForm;
